import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import ImageUploadControl from 'components/formfields/ImageUploadControl';
import { selectIsEditMode } from 'features/common/editModeSlice';
import eatingWayAreaImageService from 'features/EatingWay/eatingWayAreaImageService';
import { EatingWayAreaImage } from 'features/EatingWay/models';
import { getConfigImageSizeByAreaType } from 'features/EatingWay/utils';
import { Button, Col, Modal, Row, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EATING_WAY_AREA_IMAGE_TYPE_MAP, displayError, handleServiceError } from 'utils';
import './style.scss';

export interface EatingWayAreaImageModalProps {
  formType?: 'select' | 'update';
  eatingWayId?: number;
  eatingWayAreaImage?: Partial<EatingWayAreaImage>;
  eatingWayName?: string;
  propertyName?: string;
  areaType: number;
  onClose?: (isDataChanged: boolean) => void;
  onReflect?: (returnData: Partial<EatingWayAreaImage>) => void;
}

export function EatingWayAreaImageModal({
  formType = 'select',
  eatingWayId,
  eatingWayAreaImage,
  eatingWayName = '',
  propertyName = '',
  areaType,
  onClose,
  onReflect,
}: EatingWayAreaImageModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  const configImageSize = getConfigImageSizeByAreaType(areaType);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<Partial<EatingWayAreaImage>>({
    defaultValues: {
      id: eatingWayAreaImage?.id,
      area_type: areaType,
      eating_way_id: eatingWayId,
      image_en: eatingWayAreaImage?.image_en || '',
      image_ja: eatingWayAreaImage?.image_ja || '',
      image_ko: eatingWayAreaImage?.image_ko || '',
      image_vi: eatingWayAreaImage?.image_vi || '',
      image_zh_hans: eatingWayAreaImage?.image_zh_hans || '',
      image_zh_hant: eatingWayAreaImage?.image_zh_hant || '',
    },
  });

  function handleClose(isDataChanged: boolean = false) {
    onClose?.(isDataChanged);
  }

  function onSubmit(formValue: Partial<EatingWayAreaImage>) {
    if (formType === 'update') {
      if (eatingWayAreaImage) {
        //update eatingWayAreaImage
        try {
          (async () => {
            const response = await eatingWayAreaImageService.update(eatingWayAreaImage.id as number, formValue);
            showGlobalModal(() => (
              <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
            ));
          })();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      } else {
        //create eatingWayAreaImage
        try {
          (async () => {
            const response = await eatingWayAreaImageService.add(formValue);
            showGlobalModal(() => (
              <AlertModal title={t('App.info')} message={response.message} onClose={handleClose.bind(null, true)} />
            ));
          })();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      }
    } else {
      onReflect?.(formValue);
      onClose?.(false);
    }
  }

  return (
    <Modal size="lg" dialogClassName="eating-way-area-image-modal" show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('EatingWayAreaImageModal.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <Col sm={9} className="fw-bold">
              <Row>
                <Col sm={3} className="text-end">
                  {t('EatingWayAreaImageModal.eating_way')}:
                </Col>
                <Col sm={9}>{eatingWayName}</Col>
              </Row>
              <Row>
                <Col sm={3} className="text-end">
                  {t('EatingWayAreaImageModal.property')}:
                </Col>
                <Col sm={9}>{propertyName}</Col>
              </Row>
              <Row>
                <Col sm={3} className="text-end">
                  {t('EatingWayAreaImageModal.image_type')}:
                </Col>
                <Col sm={9}>{EATING_WAY_AREA_IMAGE_TYPE_MAP[areaType]}</Col>
              </Row>
            </Col>
            <Col sm={3} className="text-end">
              <Button type="submit" className="btn--dark-green" disabled={isSubmitting}>
                {t('EatingWayAreaImageModal.save')}
              </Button>
            </Col>
          </Row>
          <Table bordered className="form-menu-book">
            <tbody>
              <tr>
                <th style={{ width: '15%' }} rowSpan={6}>
                  {t('EatingWayAreaImageModal.area_image')}
                </th>
                <th style={{ width: '20%' }}>{t('EatingWayAreaImageModal.japanese')}</th>
                <td>
                  <ImageUploadControl
                    name="image_ja"
                    control={control}
                    minWidth={configImageSize.minWidth}
                    minHeight={configImageSize.minHeight}
                    maxWidth={configImageSize.maxWidth}
                    maxHeight={configImageSize.maxHeight}
                    maxFileSize={configImageSize.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayAreaImageModal.english')}</th>
                <td>
                  <ImageUploadControl
                    name="image_en"
                    control={control}
                    minWidth={configImageSize.minWidth}
                    minHeight={configImageSize.minHeight}
                    maxWidth={configImageSize.maxWidth}
                    maxHeight={configImageSize.maxHeight}
                    maxFileSize={configImageSize.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayAreaImageModal.simplified_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="image_zh_hans"
                    control={control}
                    minWidth={configImageSize.minWidth}
                    minHeight={configImageSize.minHeight}
                    maxWidth={configImageSize.maxWidth}
                    maxHeight={configImageSize.maxHeight}
                    maxFileSize={configImageSize.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayAreaImageModal.transitional_chinese')}</th>
                <td>
                  <ImageUploadControl
                    name="image_zh_hant"
                    control={control}
                    minWidth={configImageSize.minWidth}
                    minHeight={configImageSize.minHeight}
                    maxWidth={configImageSize.maxWidth}
                    maxHeight={configImageSize.maxHeight}
                    maxFileSize={configImageSize.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayAreaImageModal.korean')}</th>
                <td>
                  <ImageUploadControl
                    name="image_ko"
                    control={control}
                    minWidth={configImageSize.minWidth}
                    minHeight={configImageSize.minHeight}
                    maxWidth={configImageSize.maxWidth}
                    maxHeight={configImageSize.maxHeight}
                    maxFileSize={configImageSize.maxFileSize}
                  />
                </td>
              </tr>
              <tr>
                <th>{t('EatingWayAreaImageModal.vietnamese')}</th>
                <td>
                  <ImageUploadControl
                    name="image_vi"
                    control={control}
                    minWidth={configImageSize.minWidth}
                    minHeight={configImageSize.minHeight}
                    maxWidth={configImageSize.maxWidth}
                    maxHeight={configImageSize.maxHeight}
                    maxFileSize={configImageSize.maxFileSize}
                  />
                </td>
              </tr>
            </tbody>
          </Table>
        </form>
      </Modal.Body>
    </Modal>
  );
}
